import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  Stack,
  useTheme,
  IconButton,
} from "@mui/material";
import {
  WarningAmberRounded,
  CodeRounded,
  CloseRounded,
  InfoOutlined,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(245, 124, 0, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(245, 124, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 124, 0, 0); }
`;

const SubmissionReminderModal = ({ open, onClose, onSubmit }) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: { base: "16px", md: "24px" },
          padding: 0,
          maxWidth: { base: "90vw", sm: "400px", md: "460px" },
          width: "100%",
          margin: { base: 2, md: 4 },
          overflow: "hidden",
          background: "#fff",
          position: "relative",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: { base: 8, md: 16 },
          right: { base: 8, md: 16 },
          color: "#999",
          zIndex: 1, // Ensure above banner content
          backgroundColor: { base: "rgba(255,255,255,0.5)", md: "transparent" },
          "&:hover": { color: "#333", background: "#f5f5f5" },
        }}
      >
        <CloseRounded />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {/* Top Decorative Banner */}
        <Box
          sx={{
            height: { base: "120px", md: "140px" },
            background: "linear-gradient(135deg, #FF8C00 0%, #F57C00 100%)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "40px",
              background: "#fff",
              borderRadius: "50% 50% 0 0 / 100% 100% 0 0",
            },
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(8px)",
              borderRadius: "50%",
              padding: "20px",
              animation: `${float} 3s ease-in-out infinite`,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "2px solid rgba(255, 255, 255, 0.4)",
            }}
          >
            <CodeRounded sx={{ fontSize: "50px", color: "#fff" }} />
          </Box>
        </Box>

        <Box
          sx={{
            p: { base: "0 20px 24px 20px", md: "0 32px 32px 32px" },
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: "#1A1C1E",
              mb: 1,
              letterSpacing: "-0.5px",
              fontSize: { base: "20px", md: "24px" },
            }}
          >
            Submit Your Solution?
          </Typography>

          <Typography
            sx={{
              fontSize: { base: "14px", md: "15px" },
              color: "#5E6366",
              lineHeight: 1.6,
              mb: { base: 2, md: 3 },
            }}
          >
            You haven't submitted your code yet. Would you like to submit it
            before leaving this question?
          </Typography>

          <Stack
            direction="column"
            spacing={2}
            sx={{
              bgcolor: "#F8F9FA",
              p: { base: 1.5, md: 2 },
              borderRadius: "16px",
              border: "1px solid #EDEDEE",
              mb: { base: 3, md: 4 },
              textAlign: "left",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <InfoOutlined sx={{ fontSize: "20px", color: "#F57C00" }} />
              <Typography
                sx={{ fontSize: "14px", fontWeight: 600, color: "#454B4F" }}
              >
                Important Information
              </Typography>
            </Stack>
            <Typography
              sx={{ fontSize: "13px", color: "#636E72", pl: 4.2, mt: -1 }}
            >
              You can return to this question later and submit again if you have
              remaining attempts.
            </Typography>
          </Stack>

          <Stack direction="column" spacing={2} sx={{ width: "100%" }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                if (onSubmit) onSubmit();
                onClose();
              }}
              sx={{
                background: "linear-gradient(to right, #F57C00, #FF8C00)",
                color: "#fff",
                fontWeight: 700,
                fontSize: { base: "14px", md: "16px" },
                textTransform: "none",
                borderRadius: "14px",
                padding: { base: "12px", md: "16px" },
                boxShadow: "0 10px 20px -5px rgba(245, 124, 0, 0.4)",
                animation: `${pulse} 2s infinite`,
                "&:hover": {
                  background: "linear-gradient(to right, #E67600, #F57C00)",
                  boxShadow: "0 12px 24px -5px rgba(245, 124, 0, 0.5)",
                },
              }}
            >
              Submit Code
            </Button>

            <Button
              variant="text"
              fullWidth
              onClick={onClose}
              sx={{
                color: "#636E72",
                fontWeight: 600,
                fontSize: "15px",
                textTransform: "none",
                padding: "12px",
                borderRadius: "14px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                  color: "#1A1C1E",
                },
              }}
            >
              Not for now
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SubmissionReminderModal;
